import { ExternalLink } from 'lucide-react';
import type { LinkChipData } from './module-routes';

// Ported from aistudio's LinkChip.vue: a pill showing "<module>: <object>",
// with a "V<n>" version badge and an external-link icon when navigable.
// Suggestions with nothing to link to (e.g. "Add" suggestions) render as a
// plain static label instead of a dead link.
export function LinkChip({ title, value, version, href, showLink }: LinkChipData) {
  const versionLabel = version ? (/^v/i.test(version) ? version.toUpperCase() : `V${version}`) : '';

  const className = `inline-flex w-fit items-center gap-1.5 rounded-md border px-2 py-1 text-left text-sm ${
    showLink
      ? 'cursor-pointer border-border bg-background text-primary hover:bg-muted'
      : 'cursor-default border-border bg-background text-foreground'
  }`;

  const label = (
    <span className="break-words">
      {title}
      {value ? `: ${value}` : ''}
      {versionLabel && (
        <span className="ml-1.5 rounded bg-muted px-1 py-0.5 font-semibold text-[11px] text-foreground">
          {versionLabel}
        </span>
      )}
    </span>
  );

  // A real <a> (not a <button> + window.open) so the browser handles
  // middle-click/ctrl-click/"open in new tab" natively, and noopener,noreferrer
  // keeps the opened tab from getting a live `window.opener` reference back to
  // this page.
  if (showLink && href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {label}
        <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
      </a>
    );
  }

  return <span className={className}>{label}</span>;
}
